// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { SelectChangeEvent } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import { CircularProgress, FormControl, IconButton, MenuItem, Select, Typography } from '../base';
import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from '../icons';

const CountSpan = styled('span')(({ theme }) => ({
  paddingRight: theme.spacing(2),
}));

const Root = styled('div')(() => ({
  alignItems: 'center',
  display: 'flex',
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginRight: theme.spacing(2),
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

interface Props {
  readonly page: number;
  readonly pageSize: number;
  readonly currentPageSize: number | null | undefined;
  readonly hasPreviousPage: boolean;
  readonly hasNextPage: boolean;
  readonly onUpdatePage: (page: number) => void;
  readonly onUpdatePageSize?: (pageSize: number) => void;
  readonly isLoading?: boolean;
  readonly error?: string | null | undefined;
}
export default function Pager({
  page,
  pageSize,
  currentPageSize,
  hasPreviousPage,
  hasNextPage,
  isLoading,
  onUpdatePage,
  onUpdatePageSize,
  error,
}: Props): React.ReactElement {
  const onDecrementPage = React.useCallback(() => {
    onUpdatePage(page - 1);
  }, [onUpdatePage, page]);
  const onIncrementPage = React.useCallback(() => {
    onUpdatePage(page + 1);
  }, [onUpdatePage, page]);
  const onChangePageSize = React.useCallback(
    (e: SelectChangeEvent<number>) => {
      const numberVal = typeof e.target.value === 'string' ? Number.parseInt(e.target.value) : e.target.value;
      onUpdatePageSize?.(numberVal);
    },
    [onUpdatePageSize],
  );

  let start = 1;
  let end = 1;
  let totalElement = <CountSpan>0 - 0</CountSpan>;
  if (currentPageSize != null) {
    start = (page - 1) * pageSize + 1;
    end = start + currentPageSize - 1;
    if (end === 0) {
      start = 0;
    }
    totalElement = <CountSpan>{`${start} - ${end}`}</CountSpan>;
  }

  return (
    <Root>
      {error != null ? <ErrorText variant="body1">{error}</ErrorText> : null}
      {isLoading ? <StyledCircularProgress size={32} thickness={5} /> : null}
      {onUpdatePageSize != null ? (
        <FormControl sx={{ marginRight: 2 }} size="small" variant="standard">
          <Select
            labelId={'page-size-selector-label'}
            id={'page-size-selector'}
            value={pageSize}
            onChange={onChangePageSize}
            inputProps={{ 'aria-label': 'Page Size Selector' }}
          >
            {[25, 50, 100, 200].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {totalElement}
      <IconButton aria-label="previous-page" disabled={isLoading || !hasPreviousPage} onClick={onDecrementPage}>
        <KeyboardArrowLeftIcon />
      </IconButton>
      <IconButton aria-label="next-page" disabled={isLoading || !hasNextPage} onClick={onIncrementPage}>
        <KeyboardArrowRightIcon />
      </IconButton>
    </Root>
  );
}
